import React, { useState, useEffect, useRef } from 'react';

// --- PDF Page Selector Modal ---
function PDFPageSelectorModal({ pdfUrl, x, y, onClose, onInsertPages }) {
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageInput, setPageInput] = useState('');
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!pdfUrl) return;
    
    const loadPDFPages = async () => {
      try {
        setLoading(true);
        // Используем PDF.js для загрузки PDF
        const pdfjsLib = window['pdfjs-dist/build/pdf'];
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        
        const response = await fetch(pdfUrl);
        if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
        const pdf = await loadingTask.promise;
        
        const pagePromises = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          pagePromises.push(pdf.getPage(i));
        }
        
        const pdfPages = await Promise.all(pagePromises);
        const pagesData = [];
        
        for (let i = 0; i < pdfPages.length; i++) {
          const page = pdfPages[i];
          const viewport = page.getViewport({ scale: 1.0 });
          
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
          
          pagesData.push({
            pageNumber: i + 1,
            canvas: canvas,
            width: viewport.width,
            height: viewport.height
          });
        }
        
        setPages(pagesData);
        setSelectedPages([1]); // По умолчанию выбираем первую страницу
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Не удалось загрузить PDF файл');
      } finally {
        setLoading(false);
      }
    };
    
    loadPDFPages();
  }, [pdfUrl]);

  const togglePage = (pageNumber) => {
    setSelectedPages(prev => 
      prev.includes(pageNumber) 
        ? prev.filter(p => p !== pageNumber)
        : [...prev, pageNumber]
    );
  };

  const selectAll = () => {
    setSelectedPages(pages.map(p => p.pageNumber));
  };

  const selectNone = () => {
    setSelectedPages([]);
  };

  const goToPage = () => {
    const pageNum = parseInt(pageInput);
    if (pageNum >= 1 && pageNum <= pages.length && scrollContainerRef.current) {
      const pageElement = scrollContainerRef.current.querySelector(`[data-page="${pageNum}"]`);
      if (pageElement) {
        pageElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        pageElement.style.boxShadow = '0 0 0 3px #ff6b6b';
        setTimeout(() => {
          pageElement.style.boxShadow = '';
        }, 1000);
      }
      setPageInput('');
    }
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      goToPage();
    }
  };

  const handleInsert = () => {
    if (selectedPages.length === 0) return;
    
    const selectedPagesData = pages.filter(p => selectedPages.includes(p.pageNumber));
    onInsertPages(selectedPagesData, x, y);
    onClose();
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        color: 'white',
        fontSize: '18px',
        textAlign: 'center',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255,255,255,0.3)',
          borderTop: '3px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        Downloading PDF...
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
          <button 
            onClick={onClose}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '800px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          paddingBottom: '16px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>Select PDF Pages</h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={selectAll}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Select All
          </button>
          <button 
            onClick={selectNone}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Select None
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Go to page:</span>
            <input
              type="number"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyPress={handlePageInputKeyPress}
              style={{
                width: '60px',
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              min="1"
              max={pages.length}
            />
            <button 
              onClick={goToPage}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Go
            </button>
          </div>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Selected: {selectedPages.length} of {pages.length}
          </span>
        </div>

        {/* Pages Grid */}
        <div 
          ref={scrollContainerRef}
          style={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '16px',
            maxHeight: '400px'
          }}
        >
          {pages.map((page) => (
            <div
              key={page.pageNumber}
              data-page={page.pageNumber}
              onClick={() => togglePage(page.pageNumber)}
              style={{
                border: selectedPages.includes(page.pageNumber) 
                  ? '3px solid #007bff' 
                  : '1px solid #ddd',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                background: selectedPages.includes(page.pageNumber) 
                  ? '#f8f9ff' 
                  : 'white',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                fontSize: '12px',
                fontWeight: 'bold',
                marginBottom: '8px',
                textAlign: 'center',
                color: selectedPages.includes(page.pageNumber) 
                  ? '#007bff' 
                  : '#666'
              }}>
                Page {page.pageNumber}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '120px',
                overflow: 'hidden',
                borderRadius: '4px',
                background: '#f8f9fa'
              }}>
                <img
                  src={page.canvas.toDataURL()}
                  alt={`Page ${page.pageNumber}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          borderTop: '1px solid #e0e0e0',
          paddingTop: '16px'
        }}>
          <button 
            onClick={onClose}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={handleInsert}
            disabled={selectedPages.length === 0}
            style={{
              background: selectedPages.length === 0 ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: selectedPages.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Insert Selected Pages ({selectedPages.length})
          </button>
        </div>
      </div>
    </div>
  );
}

export default PDFPageSelectorModal; 